import * as TableBasedInventory from 'types/tableBasedInventory';
import * as TableBasedTemplates from 'types/tableBasedTemplates';
import { CalendarItem } from 'utils/calendarItems';
import { ElaboratedTicketGroup, WalkInCalendarParty, dashboard } from 'types';
import { Features, core } from '@tock/coretypes';
import { IServiceObject } from 'utils/getServiceDayDetails';
import { LocalDateTime } from 'js-joda';
import {
  NO_TICKET_TYPE_ID_FOR_TURN,
  SQUARE_INCREMENT_MINUTES,
} from 'constants/TableBasedTemplatesConstants';
import { PartyIdToPartyMap, partiesByIdSelector } from 'selectors/partiesById';
import { TABLE_ID_FOR_NO_TABLE } from 'constants/TableBasedInventoryConstants';
import {
  TableIdMap,
  activeAndArchivedCalendarFloorplanTablesbyIdSelector,
  calendarFloorplanTablePrioritiesByTableIdSelector,
} from 'selectors/routes/calendar/calendarFloorplanSelector';
import { assertNever } from '@tock/shared/utils/typeUtils';
import { computeSlotDetailsByDateTime } from 'utils/scanLineUtils';
import { concat, filter, flatMap, map, some, sortBy } from 'es-toolkit/compat';
import { createSelector } from 'reselect';
import { fastParseDateTime } from 'utils/timeUtils';
import { featuresSelector } from 'selectors/featuresSelector';
import {
  filteredTicketGroupsSelector,
  serviceTypesByDateTimeSelector,
} from 'selectors/ticketGroupsByDateTimeSelector';
import { findRelevantTurnTime } from 'utils/tableBasedTemplatesUtils';
import { inferServiceTypeForPurchase, inferServiceTypeForWalkInParty } from 'utils/partyUtils';
import { oc } from 'ts-optchain';
import { serviceDayTurnTimesBySizeSelector } from 'selectors/routes/table-based-templates/turnTimesBySizeSelector';
import { servicesByDaySelector } from 'selectors/service/serviceObjectsForFilteringByDaySelector';
import { timeZoneSelector } from 'selectors/timeZoneSelector';
import walkInCalendarItemSelector from 'selectors/walkInCalendarItemSelector';

/** This figures out whether a table is blocked at the provided date time. */
function isBlocked(blocks: core.ITicketGroupBlock[], tableId: number, dateTime: string) {
  return some(
    blocks,
    (block) =>
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      block.tableId === tableId && block.startDateTime! <= dateTime && block.endDateTime! > dateTime
  );
}

/**
 * This method is responsibly for pulling the calendarItems that we care about off
 * of each kind of ticket group. Specifically:
 * 1. For communal tables, we drop the first calendar item, which is the "Communal table" calendar item.
 *    In pre-flex world, this is the gray calendar item
 * 2. For time-flexible ticket groups, we filter out no-shows
 * 3. For slotted non-communal ticket groups, we just use the individual calendar item
 *
 * The resulting calendar items are then all converted into "normal calendar squares"
 */
function ticketGroupToCalendarSquares(
  tablesByKey: TableIdMap,
  tablePrioritiesByTableId: dashboard.TableAssignmentPriorityLevelByTableId[],
  turnTimes: TableBasedTemplates.TurnTimes,
  ticketGroup: ElaboratedTicketGroup,
  servicesByDay: IServiceObject[] | undefined,
  serviceTypesForFlexByDateTime: { [dateTime: string]: string },
  partiesById: PartyIdToPartyMap,
  features: Features
): TableBasedInventory.NormalDetailedSquare[] {
  const ticketGroupTicketTypeId = ticketGroup.ticketType?.[0]?.id;
  if (typeof ticketGroupTicketTypeId !== 'number') {
    return [];
  }

  if (ticketGroup.tableVariety === 'COMMUNAL_TABLE') {
    const [, ...otherCalendarItems]: CalendarItem[] = ticketGroup.calendarItems;
    return flatMap(
      filter(
        otherCalendarItems,

        (calendarItem) =>
          !calendarItem.purchase ||
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          !calendarItem.purchase.visit!.isNoShow
      ),
      (calendarItem: CalendarItem) => {
        return createNormalCalendarSquareForCalendarItem(
          calendarItem,
          ticketGroup,
          ticketGroupTicketTypeId,
          turnTimes,
          tablesByKey,
          tablePrioritiesByTableId,
          servicesByDay,
          serviceTypesForFlexByDateTime,
          partiesById,
          features
        );
      }
    );
  } else if (ticketGroup.finishedByTime) {
    const subCalendarSquares = flatMap(
      filter(
        ticketGroup.calendarItems,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        (calendarItem) => !calendarItem.purchase || !calendarItem.purchase.visit!.isNoShow
      ),
      (calendarItem) => {
        return createNormalCalendarSquareForCalendarItem(
          calendarItem,
          ticketGroup,
          ticketGroupTicketTypeId,
          turnTimes,
          tablesByKey,
          tablePrioritiesByTableId,
          servicesByDay,
          serviceTypesForFlexByDateTime,
          partiesById,
          features
        );
      }
    );

    const { renderDetailsByDateTime } = computeSlotDetailsByDateTime(subCalendarSquares);
    return flatMap(renderDetailsByDateTime, (renderDetails) => renderDetails.items);
  } else {
    const onlyCalendarItem = ticketGroup.calendarItems[0];
    return createNormalCalendarSquareForCalendarItem(
      onlyCalendarItem,
      ticketGroup,
      ticketGroupTicketTypeId,
      turnTimes,
      tablesByKey,
      tablePrioritiesByTableId,
      servicesByDay,
      serviceTypesForFlexByDateTime,
      partiesById,
      features
    );
  }
}

function computeticketGroupCalendarSquares(
  filteredTicketGroups: ElaboratedTicketGroup[],
  serviceTypesForFlexByDateTime: { [dateTime: string]: string },
  tablesByKey: TableIdMap,
  tablePrioritiesByTableId: dashboard.TableAssignmentPriorityLevelByTableId[],
  turnTimes: TableBasedTemplates.TurnTimes,
  servicesByDay: IServiceObject[] | undefined,
  partiesById: PartyIdToPartyMap,
  features: Features
): TableBasedInventory.NormalDetailedSquare[] {
  const ticketGroupsWithoutSoldNoshows = filteredTicketGroups.filter(
    (ticketGroup) =>
      ticketGroup.state !== 'SOLD' || !ticketGroup.calendarItems[0].purchase.visit?.isNoShow
  );

  const ticketGroupCalendarSquares = flatMap(ticketGroupsWithoutSoldNoshows, (ticketGroup) =>
    ticketGroupToCalendarSquares(
      tablesByKey,
      tablePrioritiesByTableId,
      turnTimes,
      ticketGroup,
      servicesByDay,
      serviceTypesForFlexByDateTime,
      partiesById,
      features
    )
  );

  return sortSquaresByStartDateTime(ticketGroupCalendarSquares);
}

/**
 * This selector takes all the ticket groups and calendar items that we have, which come from
 * the `calendar` reducer, and turns them into calendar squares.
 * This means merging information about the calendar item
 * with some other details like turn times or the service type appropriate for that start time.
 * We also splay the calendar item out onto all of its tables, so if a single purchase is assigned to three
 * tables we'll get three purchase calendar squares out. This is so that we in the next step
 * we can group the purchase onto all of its pertinent tables and render them correctly.
 */
const ticketGroupCalendarSquareSelector = createSelector(
  filteredTicketGroupsSelector,
  serviceTypesByDateTimeSelector,
  activeAndArchivedCalendarFloorplanTablesbyIdSelector,
  calendarFloorplanTablePrioritiesByTableIdSelector,
  serviceDayTurnTimesBySizeSelector,
  servicesByDaySelector,
  partiesByIdSelector,
  featuresSelector,
  computeticketGroupCalendarSquares
);

/**
 * This selector takes all the walk in calendar items, which we build off of service data,
 * and turns them into calendar squares. This means merging information about the walk-in
 * with some other details like turn times or the service type appropriate for that start time.
 * We also splay the walk-in out onto all of its tables, so if a single walk-in is assigned to three
 * tables we'll get three walk-in calendar squares out. This is so that we in the next step
 * we can group the walk-in onto all of its pertinent tables and render them correctly.
 */
const walkinCalendarSquareSelector = createSelector(
  walkInCalendarItemSelector,
  activeAndArchivedCalendarFloorplanTablesbyIdSelector,
  calendarFloorplanTablePrioritiesByTableIdSelector,
  serviceDayTurnTimesBySizeSelector,
  timeZoneSelector,
  servicesByDaySelector,
  serviceTypesByDateTimeSelector,
  (
    parties: WalkInCalendarParty[],
    tablesByKey: TableIdMap,
    tablePrioritiesByTableId: dashboard.TableAssignmentPriorityLevelByTableId[],
    turnTimes: TableBasedTemplates.TurnTimes,
    timeZone: string,
    servicesByDay: IServiceObject[] | undefined,
    serviceTypesForFlexByDateTime: { [dateTime: string]: string }
  ): TableBasedInventory.NormalDetailedSquare[] => {
    return sortSquaresByStartDateTime(
      flatMap(parties, (walkInParty): TableBasedInventory.NormalDetailedSquare[] => {
        const partySize = walkInParty.party.numPeoplePresent;
        const ticketTypeId = NO_TICKET_TYPE_ID_FOR_TURN;
        const startDateTime = fastParseDateTime(walkInParty.dateTime15MinuteInterval);
        const partyTurnTimeSec =
          walkInParty.party.leftTime && walkInParty.party.arrivalTime
            ? Math.max(0, walkInParty.party.leftTime - walkInParty.party.arrivalTime)
            : undefined;
        const actualTurnTimeMins = Math.ceil(Math.round(oc(partyTurnTimeSec)(0) / 60) / 15) * 15;
        const turnTime = findRelevantTurnTime(turnTimes, {
          startDateTime,
          partySize,
          ticketTypeId,
          variety: walkInParty?.calendarItem?.ticketGroup?.ticketTypeVariety,
        });
        const turnTimeMinutes = actualTurnTimeMins || oc(turnTime).expectedTurnTimeMinutes(0);
        return map(
          nonEmptyTableIds(walkInParty.party.tableIds),
          (tableId, idx): TableBasedInventory.NormalDetailedSquare => {
            const isDuplicate = idx > 0;
            return {
              anticipatedEndDateTime: startDateTime.plusMinutes(turnTimeMinutes),
              calendarItem: walkInParty.calendarItem,
              id: walkInParty.calendarItem.id,
              isDuplicate,
              party: walkInParty,
              primaryState: walkInParty.party.state,
              secondaryState: walkInParty.party.secondaryState,
              serviceType:
                serviceTypesForFlexByDateTime[walkInParty.dateTime15MinuteInterval] ||
                inferServiceTypeForWalkInParty(
                  {
                    arrivalTime: walkInParty.party.arrivalTime,
                    expectedReservationTime: walkInParty.party.expectedReservationTime || undefined,
                    quotedTimeMinutes: walkInParty.party.quotedTimeMinutes,
                    seatedTime: walkInParty.party.seatedTime,
                    walkInCreatedTimeSec: walkInParty.party.walkInCreatedTimeSec,
                  },
                  servicesByDay,
                  timeZone
                ),
              startDateTime,
              startDateTimeString: startDateTime.toString(),
              subType: 'WALK_IN',
              table: tableId ? tablesByKey[tableId] : undefined,
              tableId,
              tablePriorityLevel:
                tablePrioritiesByTableId.find((priorityById) => priorityById.tableId === tableId)
                  ?.priorityLevel ?? 'NEUTRAL',
              ticketGroup: undefined,
              turnTime: {
                turnTimeMinutes,
              },
              type: 'NORMAL',
            };
          }
        );
      })
    );
  }
);

/**
 * This method sorts the calendarSquares by startDateTime so that downstream, we can loop over them and know
 * that the start time is always increasing.
 */
export function sortSquaresByStartDateTime(
  squares: TableBasedInventory.NormalDetailedSquare[]
): TableBasedInventory.NormalDetailedSquare[] {
  return sortBy(squares, [
    (calendarSquare) => calendarSquare.startDateTimeString,
    (calendarSquare) =>
      !calendarSquare.calendarItem.purchase
        ? 1
        : -calendarSquare.calendarItem.purchase.ticketTypeId,
    (calendarSquare) =>
      !calendarSquare.calendarItem.purchase ? -1 : calendarSquare.calendarItem.purchase.ticketCount,
    (calendarSquare) =>
      !calendarSquare.calendarItem.purchase
        ? -1
        : calendarSquare.calendarItem.purchase.dinerPatron[0].lastName,
    (calendarSquare) =>
      !calendarSquare.calendarItem.purchase
        ? -1
        : calendarSquare.calendarItem.purchase.dinerPatron[0].firstName,
  ]);
}

function computeSortedCalendarSquares(
  ticketGroupCalendarSquares: TableBasedInventory.NormalDetailedSquare[],
  walkInSquares: TableBasedInventory.NormalDetailedSquare[]
): TableBasedInventory.NormalDetailedSquare[] {
  return sortSquaresByStartDateTime(concat(ticketGroupCalendarSquares, walkInSquares));
}

/**
 * This selector mashes together all of the calendarSquares from ticket groups with those from walk-ins.
 * The general details of this data flow can be see in this google doc:
 * https://docs.google.com/document/d/1S7ZhALbdjnKxq7mgo4I4Ogk50oZUCNekl09S2NRud2w/edit
 */
export default createSelector(
  ticketGroupCalendarSquareSelector,
  walkinCalendarSquareSelector,
  computeSortedCalendarSquares
);

/**
 * This method is only responsibly for dispatching the provided calendar item to the appropriate method
 * for constructing the calendar square. This lets us keep the construction logic for each "state" of
 * a calendar item separate.
 */
function createNormalCalendarSquareForCalendarItem(
  calendarItem: CalendarItem,
  ticketGroup: ElaboratedTicketGroup,
  ticketGroupTicketTypeId: number,
  turnTimes: TableBasedTemplates.TurnTimes,
  tablesByKey: { [tableId: string]: dashboard.Table },
  tablePrioritiesByTableId: dashboard.TableAssignmentPriorityLevelByTableId[],
  servicesByDay: IServiceObject[] | undefined,
  serviceTypesForFlexByDateTime: { [dateTime: string]: string },
  partiesById: PartyIdToPartyMap,
  features: Features
): TableBasedInventory.NormalDetailedSquare[] {
  switch (calendarItem.state) {
    case 'SOLD': {
      return createSoldCalendarSquares(
        calendarItem,
        turnTimes,
        ticketGroup,
        tablesByKey,
        tablePrioritiesByTableId,
        servicesByDay,
        serviceTypesForFlexByDateTime,
        partiesById,
        features
      );
    }
    case 'HELD': {
      return createHeldCalendarSquares(
        calendarItem,
        ticketGroupTicketTypeId,
        turnTimes,
        ticketGroup,
        tablesByKey,
        tablePrioritiesByTableId
      );
    }
    case 'LOCKED': {
      return createLockedCalendarSquares(
        calendarItem,
        ticketGroupTicketTypeId,
        turnTimes,
        ticketGroup,
        tablesByKey,
        tablePrioritiesByTableId
      );
    }
    case 'AVAILABLE':
    case 'TIME_FLEXIBLE': {
      return createAvailableCalendarSquares(
        calendarItem,
        ticketGroup,
        ticketGroupTicketTypeId,
        turnTimes,
        tablesByKey,
        tablePrioritiesByTableId
      );
    }
    case 'COMMUNAL': {
      throw new Error(
        'createNormalCalendarSquareForCalendarItem called with invalid state: ' + calendarItem.state
      );
    }
    default: {
      return assertNever(calendarItem.state);
    }
  }
}

/**
 * Converts an available calendar item to an available calendar square. This is also
 * called by "time flexible" calendar items.
 * - Splays the item out onto all of the ticket group's assigned tables, if there are any.
 * - If the ticket group is time flexible, figures out the turn time based on the table's size
 *   rather than the ticket group's allowed purchase sizes
 * - For time flexible calendar items, sets the block to "take up" only fifteen minutes. This is
 *   because for time flexible ticket groups we create a calendar item for every 15 minutes of availability.
 */
function createAvailableCalendarSquares(
  calendarItem: CalendarItem,
  ticketGroup: ElaboratedTicketGroup,
  ticketGroupTicketTypeId: number,
  turnTimes: TableBasedTemplates.TurnTimes,
  tablesByKey: { [tableId: string]: dashboard.Table },
  tablePrioritiesByTableId: dashboard.TableAssignmentPriorityLevelByTableId[]
) {
  const tableIds = calendarItem.ticketGroup.tableId;
  const ticketTypeId = ticketGroupTicketTypeId;
  const startDateTime = fastParseDateTime(calendarItem.dateTime || ticketGroup.dateTime);
  return map(nonEmptyTableIds(tableIds), (tableId): TableBasedInventory.AvailableCalendarSquare => {
    let partySize = ticketGroup.maxPurchaseSize;
    // Occupancy w/i a flex table uses the min turn time, so putting the max here makes a tail that's too long
    if (ticketGroup.finishedByTime && tablesByKey[tableId]) {
      const table = tablesByKey[tableId];
      partySize = table.minPartySize || table.size;
    }
    const turnTime = findRelevantTurnTime(turnTimes, {
      partySize,
      startDateTime,
      ticketTypeId,
      variety: ticketGroup.ticketTypeVariety,
    });

    const turnTimeMinutes = turnTime ? turnTime.expectedTurnTimeMinutes : SQUARE_INCREMENT_MINUTES;
    const anticipatedEndDateTime =
      // For flex steps, they should only be 15 minutes
      ticketGroup.finishedByTime || !turnTimeMinutes
        ? startDateTime.plusMinutes(SQUARE_INCREMENT_MINUTES)
        : startDateTime.plusMinutes(turnTimeMinutes);

    const isOfferedOnline = !ticketGroup.tableNotOfferedOnlineId.includes(tableId);
    return {
      anticipatedEndDateTime,
      availableCalendarItems: [
        {
          calendarItem,
          isBlocked:
            !!calendarItem.dateTime && isBlocked(ticketGroup.block, tableId, calendarItem.dateTime),
        },
      ],
      calendarItem,
      id: calendarItem.id,
      isOfferedOnline,
      priceCents: oc(calendarItem).ticketGroup.type[0].amount(0),
      serviceType: ticketGroup.serviceType,
      startDateTime,
      startDateTimeString: startDateTime.toString(),
      subType: 'AVAILABLE',
      tableId,
      table: tableId ? tablesByKey[tableId] : undefined,
      tablePriorityLevel:
        tablePrioritiesByTableId.find((priorityById) => priorityById.tableId === tableId)
          ?.priorityLevel ?? 'NEUTRAL',
      ticketGroup,
      turnTime: {
        turnTimeMinutes,
      },
      type: 'NORMAL',
    };
  });
}

/**
 * Converts an locked subset calendar item to a locked calendar square.
 * Nothing special going on in this case!
 * - Splays the item out onto all of the ticket group's assigned tables, if there are any.
 */
function createLockedCalendarSquares(
  calendarItem: CalendarItem,
  ticketGroupTicketTypeId: number,
  turnTimes: TableBasedTemplates.TurnTimes,
  ticketGroup: ElaboratedTicketGroup,
  tablesByKey: { [tableId: string]: dashboard.Table },
  tablePrioritiesByTableId: dashboard.TableAssignmentPriorityLevelByTableId[]
) {
  const ticketSubset = calendarItem.ticketSubset;
  const ticketTypeId = ticketSubset.purchaseVerifyInfo
    ? // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      ticketSubset.purchaseVerifyInfo.ticketTypeId!
    : ticketGroupTicketTypeId;
  const tableIds = ticketSubset.tableId;
  const partySize = ticketSubset.ticketCount;
  const startDateTime: LocalDateTime = calendarItem.dateTime
    ? fastParseDateTime(calendarItem.dateTime)
    : fastParseDateTime(ticketGroup.dateTime);
  const turnTime = findRelevantTurnTime(turnTimes, {
    partySize,
    startDateTime,
    ticketTypeId,
    variety: ticketGroup.ticketTypeVariety,
  });
  return map(
    nonEmptyTableIds(tableIds),
    (tableId, idx): TableBasedInventory.LockedCalendarSquare => {
      const isDuplicate = idx > 0;
      const startDateTime = fastParseDateTime(calendarItem.dateTime || ticketGroup.dateTime);
      const turnTimeMinutes = turnTime
        ? turnTime.expectedTurnTimeMinutes
        : SQUARE_INCREMENT_MINUTES;
      const anticipatedEndDateTime = startDateTime.plusMinutes(turnTimeMinutes);
      return {
        anticipatedEndDateTime,
        calendarItem,
        id: calendarItem.id,
        isDuplicate,
        serviceType: ticketGroup.serviceType,
        startDateTime,
        startDateTimeString: startDateTime.toString(),
        subType: 'LOCKED',
        tableId,
        table: tableId ? tablesByKey[tableId] : undefined,
        tablePriorityLevel:
          tablePrioritiesByTableId.find((priorityById) => priorityById.tableId === tableId)
            ?.priorityLevel ?? 'NEUTRAL',
        ticketGroup,
        ticketSubset,
        turnTime: {
          turnTimeMinutes,
        },
        type: 'NORMAL',
      };
    }
  );
}

/**
 * Converts an held subset calendar item to a held calendar square.
 * - Sets the anticipatedEndDateTime of the held block to be the requesteEndDateTime of
 *   the hold. Holds represent a block of time during which no party should be seated.
 * - Splays the item out onto all of the ticket group's assigned tables, if there are any.
 */
function createHeldCalendarSquares(
  calendarItem: CalendarItem,
  ticketGroupTicketTypeId: number,
  turnTimes: TableBasedTemplates.TurnTimes,
  ticketGroup: ElaboratedTicketGroup,
  tablesByKey: { [tableId: string]: dashboard.Table },
  tablePrioritiesByTableId: dashboard.TableAssignmentPriorityLevelByTableId[]
) {
  const ticketSubset = calendarItem.ticketSubset;
  const ticketTypeId = ticketGroupTicketTypeId;
  const tableIds = ticketSubset.tableId;
  const partySize = ticketSubset.ticketCount;
  const startDateTime = fastParseDateTime(calendarItem.dateTime || ticketGroup.dateTime);
  const turnTime = findRelevantTurnTime(turnTimes, {
    partySize,
    startDateTime,
    ticketTypeId,
    variety: ticketGroup.ticketTypeVariety,
  });
  const turnTimeMinutes = turnTime ? turnTime.expectedTurnTimeMinutes : SQUARE_INCREMENT_MINUTES;
  let anticipatedEndDateTime: LocalDateTime;
  if (ticketSubset.requestedEndDateTime) {
    anticipatedEndDateTime = fastParseDateTime(ticketSubset.requestedEndDateTime);
  } else {
    anticipatedEndDateTime = startDateTime.plusMinutes(turnTimeMinutes);
  }
  return map(nonEmptyTableIds(tableIds), (tableId): TableBasedInventory.HeldCalendarSquare => {
    return {
      anticipatedEndDateTime,
      calendarItem,
      id: calendarItem.id,
      isOnFlexAvailability: !!ticketGroup.finishedByTime,
      priceCents: oc(calendarItem).ticketGroup.type[0].amount(0),
      serviceType: ticketGroup.serviceType,
      startDateTime,
      startDateTimeString: startDateTime.toString(),
      subType: 'HELD',
      tableId,
      table: tableId ? tablesByKey[tableId] : undefined,
      tablePriorityLevel:
        tablePrioritiesByTableId.find((priorityById) => priorityById.tableId === tableId)
          ?.priorityLevel ?? 'NEUTRAL',
      ticketGroup,
      ticketSubset,
      turnTime: {
        turnTimeMinutes,
      },
      type: 'NORMAL',
    };
  });
}

/**
 * Converts a purchase calendar item to a sold calendar square.
 * - If the party has already been seated an left, then the visit will have
 *   an actual turn time. We use that if it exists (common if the date we're working with is in the past).
 *   We also allow the user to set the visit's expected turn time (this diner is slow / fast).
 *   In the majority case of purchases in the future, we'll figure out the turn time from
 *   the purchase's start time, party size, and experience.
 * - We infer the service type of the purchase from the service type of the ticket groups at that time.
 *   If there are multiple ticket groups with different service types at the same time, which is selected
 *   is arbitrary. If there are no ticket groups at that time, we make a best guess based on service hours.
 * - Splays the item out onto all of the ticket group's assigned tables, if there are any.
 */
function createSoldCalendarSquares(
  calendarItem: CalendarItem,
  turnTimes: TableBasedTemplates.TurnTimes,
  ticketGroup: ElaboratedTicketGroup,
  tablesByKey: { [tableId: string]: dashboard.Table },
  tablePrioritiesByTableId: dashboard.TableAssignmentPriorityLevelByTableId[],
  servicesByDay: IServiceObject[] | undefined,
  serviceTypesForFlexByDateTime: { [dateTime: string]: string },
  partiesById: PartyIdToPartyMap,
  features: Features
) {
  const purchase = calendarItem.purchase;
  const party = partiesById[purchase.originalPurchaseId];
  const partySize = purchase.ticketCount;
  const ticketTypeId = purchase.ticketTypeId;
  const startDateTimeString = calendarItem.dateTime || ticketGroup.dateTime;
  const startDateTime = fastParseDateTime(startDateTimeString);

  const actualTurnTimeMins =
    Math.ceil(Math.round(oc(purchase).visit.turnTimeSec(0) / 60) / 15) * 15;
  const turnTimeMinsOverride =
    Math.ceil(Math.round(oc(purchase).visit.turnTimeSecOverride(0) / 60) / 15) * 15;
  const turnTime = findRelevantTurnTime(turnTimes, {
    startDateTime,
    partySize,
    ticketTypeId,
    variety: ticketGroup.ticketTypeVariety,
  });
  const turnTimeMinutes =
    actualTurnTimeMins || turnTimeMinsOverride || oc(turnTime).expectedTurnTimeMinutes(0);
  const anticipatedEndDateTime = startDateTime.plusMinutes(turnTimeMinutes);

  const tableIds = party?.tableIds ?? [];

  const isOnFlexAvailability = !!ticketGroup.finishedByTime;

  return map(
    nonEmptyTableIds(tableIds),
    (tableId, idx): TableBasedInventory.BookedCalendarSquare => {
      const isDuplicate = idx > 0;
      const serviceType =
        serviceTypesForFlexByDateTime[startDateTimeString] ||
        inferServiceTypeForPurchase(purchase, servicesByDay);
      return {
        anticipatedEndDateTime,
        calendarItem,
        id: calendarItem.id,
        isDuplicate,
        isOnFlexAvailability,
        purchase,
        primaryState: party ? party.state : undefined,
        secondaryState: party ? party.secondaryState : undefined,
        serviceType,
        startDateTime,
        startDateTimeString: startDateTime.toString(),
        subType: 'BOOKED',
        tableId,
        table: tableId ? tablesByKey[tableId] : undefined,
        tablePriorityLevel:
          tablePrioritiesByTableId.find((priorityById) => priorityById.tableId === tableId)
            ?.priorityLevel ?? 'NEUTRAL',
        ticketGroup,
        turnTime: {
          turnTimeMinutes,
        },
        type: 'NORMAL',
      };
    }
  );
}

/**
 * Utility function for converting a list of table ids, which may be empty, into
 * a list of table ids which definitely has at least one value. In the case that the table
 * ids are empty, the returned list has table id `TABLE_ID_FOR_NO_TABLE`, which is -1. This is
 * so that when we key calendarSquares by tableId, the keys of the resulting object are all numeric.
 */
function nonEmptyTableIds(tableIds: number[] | undefined): [number, ...number[]] {
  if (!tableIds || tableIds.length < 1) {
    return [TABLE_ID_FOR_NO_TABLE];
  } else {
    const [first, ...rest] = tableIds;
    return [first, ...rest];
  }
}
